import React, { useState } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Clock, CheckSquare, XSquare, Eye } from 'lucide-react';
import { useReportedContent, ReportedContentItem } from '@/hooks/admin/useReportedContent';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

const ReportedContentTab: React.FC = () => {
  const { reports, isLoading, error, updateReportStatus } = useReportedContent();
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState<string | null>(null);

  const handleStatusUpdate = async (reportId: string, status: 'pending' | 'investigating' | 'resolved' | 'dismissed') => {
    setIsUpdating(reportId);
    await updateReportStatus(reportId, status);
    setIsUpdating(null);
  };

  const getStatusBadgeVariant = (status: 'pending' | 'investigating' | 'resolved' | 'dismissed'): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'investigating':
        return 'outline'; // Jaune/Orange custom?
      case 'resolved':
        return 'default'; // Vert custom?
      case 'dismissed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return <div>{t('admin.loading_reports') || 'Loading reported content...'}</div>;
  }

  if (error) {
    return <div className="text-destructive">{t('admin.error_loading_reports') || 'Error loading reports:'} {error}</div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">{t('admin.reported_content') || 'Reported Content'}</h2>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.reports.type') || 'Type'}</TableHead>
            <TableHead>{t('admin.reports.target') || 'Target'}</TableHead>
            <TableHead>{t('admin.reports.reported_by') || 'Reported By'}</TableHead>
            <TableHead>{t('admin.reports.reason') || 'Reason'}</TableHead>
            <TableHead>{t('admin.reports.details') || 'Details'}</TableHead>
            <TableHead>{t('admin.reports.date') || 'Date'}</TableHead>
            <TableHead>{t('admin.reports.status') || 'Status'}</TableHead>
            <TableHead><span className="sr-only">{t('admin.actions') || 'Actions'}</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reports.map((report) => (
            <TableRow key={report.id}>
              <TableCell><Badge variant="outline">{report.contentType}</Badge></TableCell>
              <TableCell>{report.targetName || report.targetUser || report.contentId}</TableCell>
              <TableCell>{report.reporterName || report.reportedBy}</TableCell>
              <TableCell>{report.reason}</TableCell>
              <TableCell className="max-w-xs truncate" title={report.details}>{report.details}</TableCell>
              <TableCell>{format(report.createdAt, 'Pp')}</TableCell>
              <TableCell>
                <Badge variant={getStatusBadgeVariant(report.status)}>{report.status}</Badge>
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0" disabled={isUpdating === report.id}>
                      <span className="sr-only">{t('admin.open_menu') || 'Open menu'}</span>
                      {isUpdating === report.id ? <Clock className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {report.status !== 'investigating' && (
                      <DropdownMenuItem onClick={() => handleStatusUpdate(report.id, 'investigating')}>
                        <Eye className="mr-2 h-4 w-4" /> {t('admin.actions.investigate') || 'Investigate'}
                      </DropdownMenuItem>
                    )}
                    {report.status !== 'resolved' && (
                      <DropdownMenuItem onClick={() => handleStatusUpdate(report.id, 'resolved')}>
                         <CheckSquare className="mr-2 h-4 w-4" /> {t('admin.actions.resolve') || 'Mark Resolved'}
                      </DropdownMenuItem>
                    )}
                     {report.status !== 'dismissed' && (
                      <DropdownMenuItem onClick={() => handleStatusUpdate(report.id, 'dismissed')}>
                         <XSquare className="mr-2 h-4 w-4" /> {t('admin.actions.dismiss') || 'Dismiss Report'}
                      </DropdownMenuItem>
                    )}
                     {report.status !== 'pending' && (
                      <DropdownMenuItem onClick={() => handleStatusUpdate(report.id, 'pending')}>
                         <Clock className="mr-2 h-4 w-4" /> {t('admin.actions.set_pending') || 'Set Pending'}
                      </DropdownMenuItem>
                    )}
                    {/* Ajouter action pour voir le contenu signalé */} 
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ReportedContentTab; 